import React, { useRef, useEffect, useCallback, useMemo } from "react";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface BaseWaveformProps {
  stream: MediaStream | null;
  width?: number;
  height?: number;
  className?: string;
}

interface WaveformStyle {
  primaryColor: string;
  secondaryColor?: string;
  backgroundColor?: string;
  glowColor?: string;
  strokeWidth?: number;
}

interface ProfessionalWaveformProps extends BaseWaveformProps {
  style?: Partial<WaveformStyle>;
  variant?: "bars" | "line" | "filled" | "symmetric";
  barCount?: number;
  sensitivity?: number;
  smoothing?: number;
  showPeakIndicators?: boolean;
  animationSpeed?: number;
}

interface WaveformConfig {
  fftSize: 256 | 512 | 1024 | 2048;
  smoothingTimeConstant: number;
  minDecibels: number;
  maxDecibels: number;
}

// ============================================================================
// HOOKS
// ============================================================================

const useAudioAnalyzer = (
  stream: MediaStream | null,
  config: WaveformConfig = {
    fftSize: 512,
    smoothingTimeConstant: 0.3,
    minDecibels: -90,
    maxDecibels: -10,
  }
) => {
  const analyzerRef = useRef<{
    audioContext: AudioContext;
    analyzer: AnalyserNode;
    source: MediaStreamAudioSourceNode;
  } | null>(null);

  useEffect(() => {
    if (!stream?.active) {
      if (analyzerRef.current) {
        analyzerRef.current.source.disconnect();
        analyzerRef.current.audioContext.close();
        analyzerRef.current = null;
      }
      return;
    }

    const audioContext = new AudioContext();
    const analyzer = audioContext.createAnalyser();

    analyzer.fftSize = config.fftSize;
    analyzer.smoothingTimeConstant = config.smoothingTimeConstant;
    analyzer.minDecibels = config.minDecibels;
    analyzer.maxDecibels = config.maxDecibels;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyzer);

    analyzerRef.current = { audioContext, analyzer, source };

    return () => {
      if (analyzerRef.current) {
        analyzerRef.current.source.disconnect();
        analyzerRef.current.audioContext.close();
      }
    };
  }, [
    stream,
    config.fftSize,
    config.smoothingTimeConstant,
    config.minDecibels,
    config.maxDecibels,
  ]);

  return analyzerRef.current;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const smoothArray = (data: number[], factor: number = 0.3): number[] => {
  const smoothed = [...data];
  for (let i = 1; i < smoothed.length - 1; i++) {
    smoothed[i] =
      smoothed[i] * (1 - factor) +
      ((smoothed[i - 1] + smoothed[i + 1]) * factor) / 2;
  }
  return smoothed;
};

const normalizeFrequencyData = (
  data: Uint8Array,
  sensitivity: number = 1.2,
  minHeight: number = 0.02
): number[] => {
  return Array.from(data).map((value) => {
    const normalized = (value / 255) * sensitivity;
    return Math.max(normalized, minHeight);
  });
};

// ============================================================================
// MAIN COMPONENTS
// ============================================================================

/**
 * Professional Audio Waveform - Production Ready
 * Features: Multiple variants, smooth animations, peak detection, TypeScript strict
 */
export const ProfessionalWaveform: React.FC<ProfessionalWaveformProps> = ({
  stream,
  width = 200,
  height = 60,
  className = "",
  style = {},
  variant = "bars",
  barCount = 48,
  sensitivity = 1.2,
  smoothing = 0.3,
  showPeakIndicators = false,
  animationSpeed = 60,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const peaksRef = useRef<number[]>(new Array(barCount).fill(0));
  const peakDecayRef = useRef<number[]>(new Array(barCount).fill(0));

  const defaultStyle: WaveformStyle = {
    primaryColor: "#FFC107",
    secondaryColor: "#dcC107",
    backgroundColor: "transparent",
    glowColor: "#FFC107",
    strokeWidth: 2,
  };

  const mergedStyle = useMemo(() => ({ ...defaultStyle, ...style }), [style]);

  const analyzer = useAudioAnalyzer(stream, {
    fftSize: 1024,
    smoothingTimeConstant: 0.8,
    minDecibels: -90,
    maxDecibels: -10,
  });

  const drawWaveform = useCallback(() => {
    if (!canvasRef.current || !analyzer) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size for high DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const dataArray = new Uint8Array(analyzer.analyzer.frequencyBinCount);
    analyzer.analyzer.getByteFrequencyData(dataArray);

    // Process audio data
    const step = Math.floor(dataArray.length / barCount);
    const rawData = Array.from(
      { length: barCount },
      (_, i) => dataArray[i * step] || 0
    );
    const normalizedData = normalizeFrequencyData(
      new Uint8Array(rawData),
      sensitivity
    );
    const smoothedData = smoothArray(normalizedData, smoothing);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw based on variant
    switch (variant) {
      case "bars":
        drawBars(
          ctx,
          smoothedData,
          mergedStyle,
          width,
          height,
          showPeakIndicators
        );
        break;
      case "line":
        drawLine(ctx, smoothedData, mergedStyle, width, height);
        break;
      case "filled":
        drawFilled(ctx, smoothedData, mergedStyle, width, height);
        break;
      case "symmetric":
        drawSymmetric(ctx, smoothedData, mergedStyle, width, height);
        break;
    }

    animationRef.current = requestAnimationFrame(drawWaveform);
  }, [
    analyzer,
    barCount,
    sensitivity,
    smoothing,
    variant,
    mergedStyle,
    width,
    height,
    showPeakIndicators,
  ]);

  const drawBars = (
    ctx: CanvasRenderingContext2D,
    data: number[],
    style: WaveformStyle,
    canvasWidth: number,
    canvasHeight: number,
    showPeaks: boolean
  ) => {
    const barWidth = canvasWidth / data.length;
    const centerY = canvasHeight / 2;

    data.forEach((amplitude, index) => {
      const barHeight = amplitude * canvasHeight * 0.8;
      const x = index * barWidth;
      const y = centerY - barHeight / 2;

      // Main bar with gradient
      const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
      gradient.addColorStop(0, style.primaryColor);
      gradient.addColorStop(1, style.secondaryColor || style.primaryColor);

      ctx.fillStyle = gradient;
      ctx.fillRect(x + barWidth * 0.1, y, barWidth * 0.8, barHeight);

      // Peak indicators
      if (showPeaks) {
        const currentPeak = peaksRef.current[index];
        const newPeak = Math.max(currentPeak * 0.95, amplitude);
        peaksRef.current[index] = newPeak;

        if (newPeak > 0.1) {
          ctx.fillStyle = style.primaryColor;
          ctx.fillRect(
            x + barWidth * 0.1,
            centerY - newPeak * canvasHeight * 0.4 - 2,
            barWidth * 0.8,
            2
          );
        }
      }
    });
  };

  const drawLine = (
    ctx: CanvasRenderingContext2D,
    data: number[],
    style: WaveformStyle,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const stepX = canvasWidth / (data.length - 1);
    const centerY = canvasHeight / 2;

    ctx.strokeStyle = style.primaryColor;
    ctx.lineWidth = style.strokeWidth || 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Add glow effect
    ctx.shadowColor = style.glowColor || style.primaryColor;
    ctx.shadowBlur = 10;

    ctx.beginPath();
    data.forEach((amplitude, index) => {
      const x = index * stepX;
      const y = centerY - (amplitude - 0.5) * canvasHeight * 0.8;

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    ctx.shadowBlur = 0; // Reset shadow
  };

  const drawFilled = (
    ctx: CanvasRenderingContext2D,
    data: number[],
    style: WaveformStyle,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const stepX = canvasWidth / (data.length - 1);
    const centerY = canvasHeight / 2;

    const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
    gradient.addColorStop(0, style.primaryColor + "80");
    gradient.addColorStop(0.5, style.primaryColor + "40");
    gradient.addColorStop(1, style.primaryColor + "10");

    ctx.fillStyle = gradient;

    ctx.beginPath();
    ctx.moveTo(0, centerY);

    data.forEach((amplitude, index) => {
      const x = index * stepX;
      const y = centerY - (amplitude - 0.5) * canvasHeight * 0.6;
      ctx.lineTo(x, y);
    });

    ctx.lineTo(canvasWidth, centerY);
    ctx.closePath();
    ctx.fill();
  };

  const drawSymmetric = (
    ctx: CanvasRenderingContext2D,
    data: number[],
    style: WaveformStyle,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const barWidth = canvasWidth / data.length;
    const centerY = canvasHeight / 2;

    data.forEach((amplitude, index) => {
      const barHeight = amplitude * canvasHeight * 0.4;
      const x = index * barWidth;

      // Top bar
      const topGradient = ctx.createLinearGradient(
        0,
        centerY - barHeight,
        0,
        centerY
      );
      topGradient.addColorStop(0, style.primaryColor);
      topGradient.addColorStop(1, style.primaryColor + "40");

      ctx.fillStyle = topGradient;
      ctx.fillRect(
        x + barWidth * 0.2,
        centerY - barHeight,
        barWidth * 0.6,
        barHeight
      );

      // Bottom bar
      const bottomGradient = ctx.createLinearGradient(
        0,
        centerY,
        0,
        centerY + barHeight
      );
      bottomGradient.addColorStop(0, style.primaryColor + "40");
      bottomGradient.addColorStop(1, style.primaryColor);

      ctx.fillStyle = bottomGradient;
      ctx.fillRect(x + barWidth * 0.2, centerY, barWidth * 0.6, barHeight);
    });
  };

  useEffect(() => {
    if (analyzer) {
      drawWaveform();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyzer, drawWaveform]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className={`audio-waveform ${className}`}
      aria-label="Audio waveform visualization"
    />
  );
};

// ============================================================================
// SIMPLIFIED PROFESSIONAL VERSION
// ============================================================================

/**
 * Simplified Professional Waveform - Clean and Minimal
 * Best for: Simple implementations, better performance
 */
export const SimpleProfessionalWaveform: React.FC<
  BaseWaveformProps & {
    barColor?: string;
    barCount?: number;
  }
> = ({
  stream,
  width = 250,
  height = 40,
  className = "",
  barColor = "#00d4ff",
  barCount = 32,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  const analyzer = useAudioAnalyzer(stream);

  useEffect(() => {
    if (!analyzer || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // High DPI support
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const draw = () => {
      const dataArray = new Uint8Array(analyzer.analyzer.frequencyBinCount);
      analyzer.analyzer.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, width, height);

      const barWidth = width / barCount;
      const step = Math.floor(dataArray.length / barCount);

      for (let i = 0; i < barCount; i++) {
        const amplitude = dataArray[i * step] / 255;
        const barHeight = Math.max(amplitude * height * 0.8, 2);
        const x = i * barWidth;
        const y = (height - barHeight) / 2;

        // Rounded rectangle
        ctx.fillStyle = barColor;
        ctx.beginPath();
        ctx.roundRect(
          x + barWidth * 0.2,
          y,
          barWidth * 0.6,
          barHeight,
          barWidth * 0.15
        );
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyzer, width, height, barColor, barCount]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className={`simple-waveform ${className}`}
      aria-label="Audio level indicator"
    />
  );
};

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/*
// Basic Usage:
<SimpleProfessionalWaveform 
  stream={mediaStream} 
  barColor="#00d4ff"
  width={300}
  height={50}
/>

// Advanced Usage:
<ProfessionalWaveform
  stream={mediaStream}
  width={400}
  height={80}
  variant="symmetric"
  barCount={64}
  sensitivity={1.5}
  smoothing={0.4}
  showPeakIndicators={true}
  style={{
    primaryColor: '#00d4ff',
    secondaryColor: '#004d60',
    glowColor: '#00d4ff33'
  }}
/>
*/
